import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  AdminPaymentSort,
  PaymentMethod,
  PaymentProvider,
  PaymentStatus,
  PaymentSummary,
} from '@/lib/api-admin/types';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';

export interface PaymentsQueryParams {
  page?: number;
  size?: number;
  sort?: AdminPaymentSort;
  keyword?: string;
  paymentStatus?: PaymentStatus | null;
  paymentMethod?: PaymentMethod | null;
  /** PG사(복수 = OR, 반복 파라미터). 빈 배열/없음 = 전체. */
  provider?: ReadonlyArray<string> | null;
  fromDate?: string | null;
  toDate?: string | null;
  minAmount?: number | null;
  maxAmount?: number | null;
  hasRefund?: boolean | null;
  /** true 면 고도몰 이관 결제 제외(be-payment excludeLegacy). 그 외는 보내지 않는다(이관 포함). */
  excludeLegacy?: boolean | null;
}

export interface PaymentsPage {
  items: ReadonlyArray<PaymentSummary>;
  meta: ApiMeta;
}

interface AdminPaymentListQuery {
  page?: number;
  size?: number;
  sort?: AdminPaymentSort;
  keyword?: string;
  paymentStatus?: PaymentStatus[];
  paymentMethod?: PaymentMethod[];
  provider?: PaymentProvider[];
  fromDate?: string;
  toDate?: string;
  minAmount?: number;
  maxAmount?: number;
  hasRefund?: boolean;
  excludeLegacy?: boolean;
}

export const cleanQuery = (params: PaymentsQueryParams): AdminPaymentListQuery => {
  const out: AdminPaymentListQuery = {};
  if (params.page) out.page = Math.max(0, params.page - 1);
  if (params.size) out.size = params.size;
  // sort disabled (F-08): FE enum vs BE entity column mismatch; BE @PageableDefault 사용
  if (params.keyword) out.keyword = params.keyword;
  if (params.paymentStatus) out.paymentStatus = [params.paymentStatus];
  if (params.paymentMethod) out.paymentMethod = [params.paymentMethod];
  // MANUAL(수기 결제)은 payment #14 에서 생겼고 generated 타입에는 아직 없다.
  if (params.provider && params.provider.length > 0) out.provider = [...params.provider] as PaymentProvider[];
  if (params.fromDate) out.fromDate = params.fromDate;
  if (params.toDate) out.toDate = params.toDate;
  if (params.minAmount != null) out.minAmount = params.minAmount;
  if (params.maxAmount != null) out.maxAmount = params.maxAmount;
  if (params.hasRefund != null) out.hasRefund = params.hasRefund;
  if (params.excludeLegacy === true) out.excludeLegacy = true;
  return out;
};

export const paymentsQueryKey = (params: PaymentsQueryParams) =>
  ['admin', 'payments', params] as const;

export const usePaymentsQuery = (params: PaymentsQueryParams, options: { enabled?: boolean } = {}) =>
  useQuery<PaymentsPage>({
    queryKey: paymentsQueryKey(params),
    enabled: options.enabled ?? true,
    // 필터를 바꾸는 동안 이전 결과를 그대로 두고(표가 사라졌다 나타나지 않게) 화면에 "검색 중" 을 띄운다.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // 주문번호(ORD-…/이관 16자리) 검색은 BE 가 order-service 로 orderId 를 찾아 함께 검색한다(QA-311, payment
      // release-qa v1.0.11). 종전엔 BO 가 먼저 주문 목록 검색을 한 번 더 불러 바꿔 보냈는데 중복이라 뺐다.
      const [data, meta] = await apiClient.request('get', '/api/admin/v1/payments', {
        params: { query: cleanQuery(params) },
      });
      const payload = data as { items: PaymentSummary[] };
      return { items: payload.items, meta };
    },
  });
